import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X, ChevronDown } from 'lucide-react';
import { clsx } from 'clsx';
import Button from '../common/Button';
import LanguageSwitcher from '../common/LanguageSwitcher';
import ModeToggle from '../common/ModeToggle';
import { useRegistrationEnabled } from '../../hooks/usePublicConfig';

/**
 * En-tête de la surface publique.
 *
 * Landing et FeatureLayout portaient chacun leur copie du même en-tête, qui
 * dérivaient déjà l'une de l'autre. Elles sont réunies ici : un seul endroit à
 * maintenir, et une navigation identique d'une page publique à l'autre.
 *
 * Le voile de fond passe par `.pub-header` (token) et non par un `bg-white/80` :
 * un blanc à opacité échappe au remap sombre et laissait une barre claire en
 * haut des pages en mode sombre.
 */
export default function PublicHeader() {
  const { t } = useTranslation('features');
  const { t: tc } = useTranslation('common');
  const { t: tl } = useTranslation('landing');
  const registrationEnabled = useRegistrationEnabled();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [featuresOpen, setFeaturesOpen] = useState(false);
  const featuresRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<number | null>(null);
  const location = useLocation();

  const isHome = location.pathname === '/';

  const featureLinks = [
    { label: t('dashboardLink'), href: '/fonctionnalites/dashboard' },
    { label: t('operationsLink'), href: '/fonctionnalites/operations' },
    { label: t('sharingLink'), href: '/fonctionnalites/partage' },
    { label: t('budgetsLink'), href: '/fonctionnalites/budgets' },
  ];

  const cancelClose = () => {
    if (closeTimer.current !== null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  useEffect(() => cancelClose, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setFeaturesOpen(false);
  }, [location.pathname]);

  /**
   * Fermeture du menu « Fonctionnalités ».
   *
   * Un `onBlur` différé fermait le menu 150 ms après la perte de focus du
   * bouton : le blur part dès le `mousedown`, donc tout clic plus lent que
   * ce délai démontait le lien avant l'émission du `click` et la navigation
   * ne partait pas. On écoute le `pointerdown` hors du menu à la place, ce
   * qui ne dépend plus de la durée du clic.
   */
  useEffect(() => {
    if (!featuresOpen) return;

    const handlePointerDown = (e: PointerEvent) => {
      if (!featuresRef.current?.contains(e.target as Node)) {
        cancelClose();
        setFeaturesOpen(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFeaturesOpen(false);
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [featuresOpen]);

  /**
   * Ouverture au survol, souris uniquement : au doigt, `pointerenter` part avec
   * le tap et rouvrirait aussitôt le menu que le `click` vient de fermer.
   * La fermeture est différée de 150 ms pour tolérer une sortie latérale brève ;
   * le trajet bouton → menu, lui, ne coupe pas le survol (cf. le `pt-2` du
   * conteneur du panneau, qui comble le gap visuel).
   */
  const handleFeaturesEnter = (e: React.PointerEvent) => {
    if (e.pointerType !== 'mouse') return;
    cancelClose();
    setFeaturesOpen(true);
  };

  const handleFeaturesLeave = (e: React.PointerEvent) => {
    if (e.pointerType !== 'mouse') return;
    cancelClose();
    closeTimer.current = window.setTimeout(() => setFeaturesOpen(false), 150);
  };

  const navLink = (active: boolean) =>
    clsx(
      'text-sm font-medium transition-colors',
      active ? 'text-primary-600' : 'pub-muted hover:text-primary-600'
    );

  const mobileLink = (active: boolean) =>
    clsx(
      'block px-3 py-2 text-sm font-medium rounded-lg transition-colors',
      active ? 'text-primary-600' : 'pub-muted hover:text-primary-600'
    );

  return (
    <header
      className={clsx(
        'pub-header fixed top-0 left-0 right-0 z-50',
        mobileMenuOpen && 'pub-header--open'
      )}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="pub-wordmark text-2xl font-bold text-primary-600">
            Splinity
            <span className="pub-subtle text-[10px] font-normal ml-1">
              ({__APP_STAGE__} v{__APP_VERSION__})
            </span>
          </Link>

          {/* Navigation desktop */}
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/" className={navLink(isHome)}>
              {tc('nav.home')}
            </Link>
            <div
              className="relative"
              ref={featuresRef}
              onPointerEnter={handleFeaturesEnter}
              onPointerLeave={handleFeaturesLeave}
            >
              <button
                onClick={() => setFeaturesOpen(!featuresOpen)}
                aria-expanded={featuresOpen}
                aria-haspopup="true"
                className={clsx(
                  'flex items-center gap-1',
                  navLink(location.pathname.startsWith('/fonctionnalites'))
                )}
              >
                {tc('nav.features')}
                <ChevronDown
                  className={clsx('w-4 h-4 transition-transform', featuresOpen && 'rotate-180')}
                />
              </button>
              {featuresOpen && (
                <div className="absolute top-full left-0 pt-2 w-52 z-50">
                  <div className="pub-card py-1">
                    {/* L'ancre ne mène quelque part que depuis la page d'accueil. */}
                    {isHome && (
                      <a
                        href="#fonctionnalites"
                        onClick={() => setFeaturesOpen(false)}
                        className="block px-4 py-2 text-sm pub-subtle hover:text-primary-600 transition-colors border-b pub-rule"
                      >
                        {tl('features.viewAll')}
                      </a>
                    )}
                    {featureLinks.map((link) => (
                      <Link
                        key={link.href}
                        to={link.href}
                        onClick={() => setFeaturesOpen(false)}
                        className="block px-4 py-2 text-sm pub-muted hover:text-primary-600 transition-colors"
                      >
                        {link.label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <Link to="/tarifs" className={navLink(location.pathname === '/tarifs')}>
              {tc('nav.pricing')}
            </Link>
            <Link to="/contact" className={navLink(location.pathname === '/contact')}>
              {tc('nav.contact')}
            </Link>
          </nav>

          <div className="flex items-center gap-1 sm:gap-2">
            <ModeToggle />
            <LanguageSwitcher variant="icon" className="hidden md:block" />
            {registrationEnabled === true && (
              <Link to="/register" className="hidden md:block ml-1">
                <Button variant="outline" size="sm">
                  {tl('hero.createAccount')}
                </Button>
              </Link>
            )}
            <Link to="/login" className="ml-1">
              <Button variant="primary" size="sm">
                {tl('hero.signIn')}
              </Button>
            </Link>

            {/* Menu mobile */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? tc('nav.closeMenu') : tc('nav.openMenu')}
              aria-expanded={mobileMenuOpen}
              className="md:hidden p-2 rounded-lg pub-muted hover:text-primary-600 transition-colors"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Navigation mobile */}
        {mobileMenuOpen && (
          <div className="md:hidden pb-4 border-t pub-rule pt-4 space-y-1">
            <Link to="/" onClick={() => setMobileMenuOpen(false)} className={mobileLink(isHome)}>
              {tc('nav.home')}
            </Link>
            {featureLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={clsx(mobileLink(location.pathname === link.href), 'pl-6')}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/tarifs"
              onClick={() => setMobileMenuOpen(false)}
              className={mobileLink(location.pathname === '/tarifs')}
            >
              {tc('nav.pricing')}
            </Link>
            <Link
              to="/contact"
              onClick={() => setMobileMenuOpen(false)}
              className={mobileLink(location.pathname === '/contact')}
            >
              {tc('nav.contact')}
            </Link>
            {registrationEnabled === true && (
              <Link
                to="/register"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-3 py-2 text-sm font-medium text-primary-600 rounded-lg"
              >
                {tl('hero.createAccount')}
              </Link>
            )}
            <div className="border-t pub-rule mt-2 pt-2">
              <LanguageSwitcher variant="full" />
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
